/* eslint-disable */

import type { StoreApi, UseBoundStore } from "zustand";

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

export const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S,
) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    (store.use as Record<string, () => unknown>)[k] = () =>
      store((s) => s[k as keyof typeof s]);
  }

  return store;
};

type NestedSelectors<T> = {
  [K in keyof T]: () => T[K];
};

export type StoreWithNestedSlices<
  TStore extends UseBoundStore<StoreApi<any>>,
  TSliceKeys extends string,
> = TStore extends { use: infer TUse } & UseBoundStore<StoreApi<infer TState>>
  ? Omit<TStore, "use"> & {
      use: {
        [K in keyof TUse]: K extends TSliceKeys
          ? NestedSelectors<TState[K & keyof TState]>
          : TUse[K];
      };
    }
  : never;

export function createNestedSliceSelectors<
  TStore extends UseBoundStore<StoreApi<any>>,
  TSliceKeys extends readonly string[],
>(
  store: TStore,
  sliceKeys: TSliceKeys,
): StoreWithNestedSlices<TStore, TSliceKeys[number]> {
  function createNestedSelector(sliceKey: string) {
    return new Proxy(
      {},
      {
        get: (_target, prop: string) => {
          return () =>
            store((state) => {
              const slice = state[sliceKey] as unknown as Record<
                string,
                unknown
              >;
              return slice[prop];
            });
        },
      },
    );
  }

  const useObj = (store as any).use as Record<string, unknown>;
  for (const sliceKey of sliceKeys) {
    useObj[sliceKey] = createNestedSelector(sliceKey);
  }

  return store as StoreWithNestedSlices<TStore, TSliceKeys[number]>;
}
